/**
 * Drawings of each symbol in localStorage (TV-21).
 * Key: binify.chart.drawings.<SYMBOL>, JSON with a schema version like the chart settings.
 * A broken record is left out, the others still load; nothing broken reaches the engine.
 */

import { DRAWING_MAX_WIDTH_PX, DRAWING_MIN_WIDTH_PX } from "../engine/drawings/drawingConstants";
import { DRAWING_LEVEL_FORMAT } from "../engine/drawings/drawingLevels";
import { isValidLevelValue, PITCHFORK_LEVEL_COUNT, STYLE_FIELD } from "../engine/drawings/drawingStyles";
import { getDrawingTool } from "../engine/drawings/drawingTools";
import { isPlainObject } from "./savedIndicators";

// Fields added later load with the defaults of the tool, so the old records stay valid as they are
const SCHEMA_VERSION = 1;
const LEVEL_FORMATS = Object.values(DRAWING_LEVEL_FORMAT);

// The slug of one pair can come in different case (URL, backend list), it is one set of drawings
const storageKey = (symbol) => `binify.chart.drawings.${String(symbol).toUpperCase()}`;

const parsePoint = (value) =>
  isPlainObject(value) && Number.isFinite(value.time) && Number.isFinite(value.price)
    ? { time: value.time, price: value.price }
    : null;

const parsePoints = (value, pointCount) => {
  if (!Array.isArray(value) || value.length !== pointCount) return null;

  const points = value.map(parsePoint);

  return points.every(Boolean) ? points : null;
};

const isValidWidth = (width) =>
  Number.isInteger(width) && width >= DRAWING_MIN_WIDTH_PX && width <= DRAWING_MAX_WIDTH_PX;

const isValidColor = (color) => typeof color === "string" && Boolean(color);

const isValidLevel = (level) =>
  isPlainObject(level) &&
  isValidLevelValue(level.value) &&
  isValidColor(level.color) &&
  isValidWidth(level.width) &&
  typeof level.isVisible === "boolean";

const FIELD_CHECKS = {
  [STYLE_FIELD.COLOR]: isValidColor,
  [STYLE_FIELD.FLAG]: (flag) => typeof flag === "boolean",
  [STYLE_FIELD.LEVEL_FORMAT]: (format) => LEVEL_FORMATS.includes(format),
  [STYLE_FIELD.LEVELS]: (levels) =>
    Array.isArray(levels) && levels.length === PITCHFORK_LEVEL_COUNT && levels.every(isValidLevel),
};

const BASE_STYLE_CHECKS = { color: isValidColor, width: isValidWidth };

// Only what is valid: the engine fills the rest with the defaults of the tool
const parseStyle = (value, tool) => {
  if (!isPlainObject(value)) return {};

  const checks = { ...BASE_STYLE_CHECKS };

  Object.entries(tool.style?.fields || {}).forEach(([key, kind]) => {
    checks[key] = FIELD_CHECKS[kind];
  });

  return Object.fromEntries(Object.keys(checks).filter((key) => checks[key](value[key])).map((key) => [key, value[key]]));
};

const parseDrawing = (value) => {
  const tool = isPlainObject(value) ? getDrawingTool(value.type) : null;
  const points = tool && parsePoints(value.points, tool.pointCount);

  if (!points) return null;

  return {
    type: value.type,
    points,
    style: parseStyle(value.style, tool),
    isLocked: value.isLocked === true,
    isVisible: value.isVisible !== false,
  };
};

/**
 * @param {*} value - parsed from localStorage
 * @returns {Array<{ type: string, points: object[], style: object, isLocked: boolean, isVisible: boolean }>}
 *   in the order they were drawn; an empty list for anything else
 */
export const parseSavedDrawings = (value) =>
  Array.isArray(value) ? value.map(parseDrawing).filter(Boolean) : [];

/**
 * What is kept of a drawing of the engine list: the id is given anew on load,
 * the selection is not kept at all.
 *
 * @param {object} drawing - item of the engine list
 * @returns {{ type: string, points: object[], style: object, isLocked: boolean, isVisible: boolean }}
 */
export const toSavedDrawing = ({ type, points, style, isLocked, isVisible }) => ({
  type,
  points,
  style,
  isLocked,
  isVisible,
});

/** What the engine saves its drawings through (drawings/drawingPersistence.js). */
export const drawingsStorage = {
  /**
   * @param {string} symbol
   * @returns {object[]} see parseSavedDrawings; an empty list when nothing can be read
   */
  read: (symbol) => {
    try {
      const parsed = JSON.parse(localStorage.getItem(storageKey(symbol)));

      return parseSavedDrawings(isPlainObject(parsed) ? parsed.drawings : null);
    } catch (_e) {
      return [];
    }
  },

  /**
   * @param {object[]} drawings - items of the engine list
   * @returns {string|null} what `write` takes; null for no drawings
   */
  serialize: (drawings) =>
    drawings.length ? JSON.stringify({ version: SCHEMA_VERSION, drawings: drawings.map(toSavedDrawing) }) : null,

  /**
   * @param {string} symbol
   * @param {string|null} serialized - from `serialize`; null removes the key
   * @returns {boolean} false when the browser refused, e.g. the storage quota
   */
  write: (symbol, serialized) => {
    try {
      if (serialized === null) localStorage.removeItem(storageKey(symbol));
      else localStorage.setItem(storageKey(symbol), serialized);

      return true;
    } catch (_e) {
      return false;
    }
  },
};
